/**
 * PublicAppointmentConfirmationAPI.js
 *
 * Client for the public (unauthenticated) Appointment Confirmation V2/V3 endpoints.
 * Used by PublicAppointmentConfirmationPage at /confirmar/:companySlug/:token
 *
 * Endpoints (see docs/features/public-appointment-confirmation/guide.md):
 *   - public/appointment-confirmation/get.php     (validate token + fetch data)
 *   - public/appointment-confirmation/respond.php (record patient action)
 *   - public/schedule/get_available_slots.php      (V3 — fetch available slots for reschedule)
 */

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';
const PUBLIC_CONFIRMATION_BASE = BASE_URL + 'public/appointment-confirmation/';

/**
 * Fetches the appointment info for the given confirmation token.
 *
 * @param {string} token
 * @param {(res: {
 *   success: boolean,
 *   status: 'active' | 'used' | 'expired' | 'invalid' | 'error',
 *   appointment?: {
 *     clinic_name: string,
 *     professional_name: string,
 *     patient_first_name: string,
 *     date: string,          // YYYY-MM-DD
 *     time: string,          // HH:mm
 *   },
 *   action?: 'confirm' | 'cancel' | 'reschedule',
 *   error?: string,
 * }) => void} callback
 */
export const GetPublicAppointmentConfirmationAPI = (token, callback) => {
    const url = PUBLIC_CONFIRMATION_BASE + 'get.php';
    const body = 'token=' + encodeURIComponent(token || '');

    fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    })
        .then((res) => res.json())
        .then((data) => callback(data))
        .catch(() =>
            callback({
                success: false,
                status: 'error',
                error: 'Erro de conexão. Verifique sua internet e tente novamente.',
            })
        );
};

/**
 * Submits the patient action for the given confirmation token.
 *
 * @param {string} token
 * @param {'confirm' | 'cancel' | 'reschedule'} action
 * @param {(res: {
 *   success: boolean,
 *   status: 'done' | 'invalid' | 'expired' | 'used' | 'not_actionable' | 'error',
 *   action?: 'confirm' | 'cancel' | 'reschedule',
 *   error?: string,
 * }) => void} callback
 */
export const RespondPublicAppointmentConfirmationAPI = (token, action, callback) => {
    const url = PUBLIC_CONFIRMATION_BASE + 'respond.php';
    const body =
        'token=' + encodeURIComponent(token || '') +
        '&action=' + encodeURIComponent(action || '');

    fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    })
        .then((res) => res.json())
        .then((data) => callback(data))
        .catch(() =>
            callback({
                success: false,
                status: 'error',
                error: 'Erro de conexão. Verifique sua internet e tente novamente.',
            })
        );
};

// ─── V5 — Direct reschedule (creates new appointment) ────────────────────────

/**
 * Reschedules an appointment directly via the public confirmation link.
 * Marks the original as 'rescheduled' and creates a new one at the chosen slot.
 *
 * @param {string} token
 * @param {string} newDate  YYYY-MM-DD
 * @param {string} newTime  HH:mm
 * @param {(res: {
 *   success: boolean,
 *   status: 'done' | 'invalid' | 'expired' | 'used' | 'not_actionable' | 'slot_unavailable' | 'error',
 *   action?: 'reschedule',
 *   new_appointment_id?: number,
 *   error?: string,
 * }) => void} callback
 */
export const ReschedulePublicAppointmentAPI = (token, newDate, newTime, callback) => {
    const url = PUBLIC_CONFIRMATION_BASE + 'reschedule.php';
    const body =
        'token=' + encodeURIComponent(token || '') +
        '&new_date=' + encodeURIComponent(newDate || '') +
        '&new_time=' + encodeURIComponent(newTime || '');

    fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    })
        .then((res) => res.json())
        .then((data) => callback(data))
        .catch(() =>
            callback({
                success: false,
                status: 'error',
                error: 'Erro de conexão. Verifique sua internet e tente novamente.',
            })
        );
};

// ─── V3 — Reschedule slot picker ─────────────────────────────────────────────

const PUBLIC_SCHEDULE_BASE = BASE_URL + 'public/schedule/';

/**
 * Fetches available slots for a single date using the public schedule endpoint.
 * Used by the V3 reschedule slot picker to collect suggestions across multiple days.
 *
 * @param {string} companySlug
 * @param {string|number} professionalId
 * @param {string} date  YYYY-MM-DD
 * @param {number} procedureDuration  minutes (0 = use company default)
 * @param {(res: {
 *   success: boolean,
 *   date?: string,
 *   slots?: Array<{ time: string, available: boolean }>,
 *   error?: string,
 * }) => void} callback
 */
export const GetPublicAvailableSlotsAPI = (companySlug, professionalId, date, procedureDuration, callback) => {
    const params = new URLSearchParams({
        company_slug: companySlug,
        professional_id: String(professionalId),
        date: date,
    });
    if (procedureDuration > 0) {
        params.set('procedure_duration', String(procedureDuration));
    }

    const url = PUBLIC_SCHEDULE_BASE + 'get_available_slots.php?' + params.toString();

    fetch(url)
        .then((res) => res.json())
        .then((data) => callback(data))
        .catch(() =>
            callback({
                success: false,
                error: 'Erro de conexão ao buscar horários disponíveis.',
            })
        );
};

/**
 * Fetches the next N available slots across multiple days (up to maxDays ahead).
 * Iterates day by day, collecting available slots until reaching the target count.
 *
 * @param {object} params
 * @param {string} params.companySlug
 * @param {string|number} params.professionalId
 * @param {number} params.procedureDuration  minutes
 * @param {string} params.startDate  YYYY-MM-DD (exclusive — starts from day after)
 * @param {number} [params.maxSlots=6]  stop after collecting this many
 * @param {number} [params.maxDays=14]  search window in days
 * @param {(res: {
 *   success: boolean,
 *   slots: Array<{ date: string, time: string }>,
 *   error?: string,
 * }) => void} callback
 */
export const GetRescheduleAvailableSlotsAPI = ({
    companySlug,
    professionalId,
    procedureDuration,
    startDate,
    maxSlots = 6,
    maxDays = 14,
}, callback) => {
    if (!companySlug || !professionalId) {
        callback({ success: false, slots: [], error: 'Dados insuficientes para buscar horários.' });
        return;
    }

    const collected = [];
    let daysChecked = 0;

    // Helper: add N days to a YYYY-MM-DD string
    const addDays = (dateStr, n) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + n);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${yy}-${mm}-${dd}`;
    };

    const fetchNext = () => {
        if (daysChecked >= maxDays || collected.length >= maxSlots) {
            callback({ success: true, slots: collected });
            return;
        }

        const dayDate = addDays(startDate, daysChecked + 1); // start from day after
        daysChecked++;

        GetPublicAvailableSlotsAPI(companySlug, professionalId, dayDate, procedureDuration, (res) => {
            if (res && res.success && Array.isArray(res.slots)) {
                const available = res.slots.filter((s) => s.available);
                for (const slot of available) {
                    if (collected.length >= maxSlots) break;
                    collected.push({ date: dayDate, time: slot.time });
                }
            }
            // Continue to next day regardless of error on this day
            fetchNext();
        });
    };

    fetchNext();
};
